import { ConfirmDeleteModalComponent } from '$core/modal/confirm-delete/confirm-delete.component';
import { SessionService } from '$core/services/session.service';
import { SwalService } from '$core/shell/swal/swal.service';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  OnInit,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  first,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  BatchCommit,
  BatchCommitedComponent,
} from '../../modals/batch-commited/batch-commited.component';
import {
  Batch,
  BatchDepartureByIdRespWithSelected,
  BatchDepartureResp,
  BatchDocumentStatus,
  DuplicateType,
  BatchStatusEnum,
} from '../../models';
import {
  BatchesParseStorageService,
  BatchesService,
  BatchMailingIndexParams,
} from '../../services';
import { PaginationParams } from '$shared/components/paginator/paginator.component';
import * as dayjs from 'dayjs';
import { HttpErrorResponse } from '@angular/common/http';
import { BatchCommitErrorModalComponent } from '../../modals/batch-commit-error-modal/batch-commit-error-modal.component';
import { FormControl, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  createFailureState,
  createLoadingState,
  createSuccessState,
} from '$core/util';
import {
  BatchDocumentsStoreService,
  BatchDocumentToastService,
  BatchDocumentsStoreItem,
  BatchDocumentsService,
  BatchRefreshService,
} from '$core/services';
import { GenerateOneBatchDocumentEventParams } from '../../services/batches/generate-batch-document-params';
import { DocumentType } from '../../models/document-type';

@Component({
  selector: 'app-view-batch',
  templateUrl: './view-batch.component.html',
  styleUrls: ['./view-batch.component.scss'],
})
export class ViewBatchComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(BsModalService);
  private readonly sessionService = inject(SessionService);
  private readonly batchesService = inject(BatchesService);
  private readonly batchStorageService = inject(BatchesParseStorageService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly renderer = inject(Renderer2);
  private readonly swalService = inject(SwalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly batchDocumentsService = inject(BatchDocumentsService);
  private readonly batchDocumentsStoreService = inject(
    BatchDocumentsStoreService
  );
  private readonly batchDocumentToastService = inject(
    BatchDocumentToastService
  );
  private readonly batchRefreshService = inject(BatchRefreshService);

  /**
   * The action menu in batch header section.
   */
  @ViewChild('actionMenu') private actionMenu?: BsDropdownDirective;

  public readonly BatchStatusEnum = BatchStatusEnum;
  public readonly session$ = this.sessionService.session$;
  public readonly faTrashAlt = faTrashAlt;
  public readonly BatchDocumentStatus = BatchDocumentStatus;
  public readonly dayjs = dayjs;

  /**
   * The batch data.
   */
  protected readonly batch = signal<Batch>({} as Batch);

  /**
   * Indicates whether the batch is loading.
   */
  protected readonly isBatchLoading = signal(false);

  /**
   * The state of the all labels request.
   */
  protected readonly loadingAllLabelsRequestState = computed(() => {
    const batch = this.batch();

    if (batch) {
      const documentsStatus = batch.documents?.status;

      if (documentsStatus === BatchDocumentStatus.PROCESSING) {
        return BatchDocumentStatus.PROCESSING;
      }

      const storeItem = this.batchDocumentsStoreService.findSpecificStoreItem(
        { batchId: this.batch().id },
        true
      );

      return storeItem ? storeItem.documentStatus : 'idle';
    }

    return 'idle';
  });

  /**
   * Indicates whether some label in batch is loading.
   */
  protected readonly isSomeLabelLoading = computed(() => {
    if (this.batch() && this.batchDepartures()) {
      const isSomeDepartureDocumentLoading = this.batchDepartures()?.data.some(
        (item) =>
          item.documents.some((document) => document.status === 'processing')
      );
      if (isSomeDepartureDocumentLoading) {
        return true;
      }

      const loadingDocumentsStore =
        this.batchDocumentsStoreService.loadingBatchDocumentsStore();
      return (
        loadingDocumentsStore[loadingDocumentsStore.length - 1] || []
      ).some(
        (item) =>
          item.batchId === this.batch().id &&
          item.documentStatus === 'processing'
      );
    }
    return false;
  });

  /**
   * The state of the label size update request.
   */
  protected readonly labelSizeUpdateRequestState =
    this.batchStorageService.labelSizeUpdateRequestState;

  protected readonly labelSizePlaceholder = `210x150 ${this.translateService.instant('MM_SUFFIX')}`;

  /**
   * The available label sizes.
   */
  protected readonly availableLabelSizes = computed(() =>
    (
      this.batchStorageService.labelSizes()?.[this.batch().postal_delivery_type]
        ?.sizes ?? []
    ).map((size) => ({
      value: size,
      label: `${size} ${this.translateService.instant('MM_SUFFIX')}`,
    }))
  );

  /**
   * The control for the label size select.
   */
  protected readonly labelSizeControl = new FormControl<string | null>(null, [
    Validators.required,
  ]);

  /**
   * Indicates whether the download all labels button is disabled.
   */
  protected readonly isDownloadAllLabelsDisabled = computed(() => {
    return (
      this.loadingAllLabelsRequestState() === BatchDocumentStatus.PROCESSING ||
      this.isSomeLabelLoading() ||
      this.availableLabelSizes().length === 0 ||
      this.labelSizeUpdateRequestState().state === 'loading'
    );
  });

  /**
   * Departures of the viewed batch.
   */
  protected readonly batchDepartures = this.batchStorageService.batchDepartures;

  public isLoadingCommited$ = new BehaviorSubject<boolean>(false);
  public isFullScreen$ = this.batchStorageService.isFullScreen$;
  public isBatchDeparturesLoading$ = new BehaviorSubject(false);
  public isCommitBtnShow$ = new BehaviorSubject<boolean>(true);
  public activeTab = 2;
  public queryParams: BatchMailingIndexParams = {} as BatchMailingIndexParams;
  public batchId = this.activatedRoute.snapshot.params['id'];

  constructor() {
    effect(() => {
      const labelSizes = this.availableLabelSizes();
      if (labelSizes.length > 0) {
        this.labelSizeControl.enable();
      } else {
        this.labelSizeControl.disable();
      }
    });

    this.labelSizeControl.valueChanges
      .pipe(
        tap((value) => this.batchStorageService.selectedLabelSize.set(value)),
        filter(
          (value) => value && this.batch() && value !== this.batch().label_size
        ),
        tap(() => this.labelSizeUpdateRequestState.set(createLoadingState())),
        switchMap((value) => {
          return this.batchesService.updateBatch(this.batch().id, {
            label_size: value,
          });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (batch) => {
          this.batch.set(batch);
          this.labelSizeUpdateRequestState.set(createSuccessState(batch));
        },
        error: (error) => {
          this.labelSizeUpdateRequestState.set(createFailureState(error));
        },
      });

    this.batchStorageService.generateDepartureDocument$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.formDocument(params);
      });

    this.listenToBatchRefresh();
  }

  @HostListener('document:keydown.escape')
  public onEscapeKeyPress() {
    if (this.isFullScreen$.getValue()) {
      this.toggleFullScreen();
    }
  }

  public ngOnInit() {
    this.getBatch(this.batchId);
    this.listenToRouter();
  }

  public toggleFullScreen() {
    this.batchStorageService.toggleFullScreen(this.renderer);
    this.actionMenu?.hide();
  }

  public formAllLabels(): void {
    const batch = this.batch();

    if (batch.documents !== null) {
      switch (batch.documents.status) {
        case BatchDocumentStatus.DONE:
          this.downloadDocuments(
            batch.documents.id,
            this.runAllLabelsGeneration
          );
          return;
        case BatchDocumentStatus.ERROR:
          this.runAllLabelsGeneration();
          break;
        case BatchDocumentStatus.PROCESSING:
          return;
        default:
          return;
      }
    } else {
      this.runAllLabelsGeneration();
    }
  }

  public commit(): void {
    this.isLoadingCommited$.next(true);
    this.batchesService
      .commitBatch(this.batch().id)
      .pipe(
        switchMap((batch) => {
          this.batch.set(batch);

          return this.batchesService.batchMailingIndex({
            batchId: batch.id,
            perPage: this.queryParams.perPage,
          });
        }),
        finalize(() => {
          this.isLoadingCommited$.next(false);
        })
      )
      .subscribe({
        next: (batchDepartures) => {
          this.batchDepartures.set(batchDepartures);
          this.openSuccessCommitModal();
          this.isCommitBtnShow$.next(false);
        },
        error: (error: HttpErrorResponse) => {
          this.openErrorCommitModal(error);
        },
      });
  }

  private openErrorCommitModal(error: HttpErrorResponse) {
    this.modalService.show(BatchCommitErrorModalComponent, {
      ignoreBackdropClick: false,
      class: 'modal-dialog-centered',
      initialState: {
        errorData: error.error,
      },
    });
  }

  public duplicate(type: DuplicateType): void {
    this.actionMenu?.hide();
    this.batchesService
      .duplicate(this.batch().id, type)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.router
          .navigate(['/personal/batch/batches/', res.id, 'view-batch'])
          .then(() => this.getBatch(res.id));
      });
  }

  public removeBatch(): void {
    this.actionMenu?.hide();
    this.modalService.show(ConfirmDeleteModalComponent, {
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered',
      initialState: {
        title:
          this.translateService.instant(
            'BATCH_MAILING.BATCHES_TAB.ACTIONS.DELETE_ONE_BATCH'
          ) + ' ?',
        subTitle: this.batchDepartures().data.length
          ? this.translateService.instant(
              'BATCH_MAILING.BATCHES_TAB.BATCH_CONTAINS_DEPARTURES'
            )
          : this.translateService.instant(
              'BATCH_MAILING.BATCHES_TAB.ACTIONS.CONFIRM_DELETING'
            ),
        submitBtnClass: 'button--filled',
        cancelBtnClass: 'button--text',
      },
    });

    this.modalService.onHidden
      .pipe(
        first(),
        filter((res) => res === 'delete'),
        switchMap(() =>
          this.batchesService.remove({ batchId: this.batch().id })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.router.navigate(['/personal/batch/batches']).then(() => {
          const swal = this.swalService.swal;
          swal.title = this.translateService.instant(
            'BATCH_MAILING.BATCHES_TAB.ACTIONS.DELETED_BATCH'
          );
          swal.icon = 'success';
          return swal.fire();
        });
      });
  }

  private openSuccessCommitModal(): void {
    const modal = this.modalService.show(BatchCommitedComponent, {
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered',
      initialState: {
        item: this.batch(),
      },
    });

    modal.content.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: BatchCommit) => {
        return this.router.navigate([`/personal/batch/${result.link}`], {
          relativeTo: this.activatedRoute,
        });
      });
  }

  private getBatch(id: string | number): void {
    this.isBatchLoading.set(true);
    this.batchesService
      .show({ batchId: id })
      .pipe(
        map((el) => {
          /*  el.formBlanks =
            PAYMENT_TYPE_DATA[el.postal_delivery_type].formBlanks || null; */
          return el;
        }),
        switchMap((res) => {
          this.batch.set(res);

          const defaultLabelSize =
            this.batchStorageService.labelSizes()?.[
              this.batch().postal_delivery_type
            ]?.default_size;
          this.labelSizeControl.setValue(
            this.batch().label_size || defaultLabelSize || null
          );

          this.labelSizeUpdateRequestState.set(
            createSuccessState(this.batch())
          );
          if (!this.queryParams.search && !this.queryParams.perPage) {
            return this.batchesService.batchMailingIndex({ batchId: id });
          }
          return of(null);
        }),
        finalize(() => {
          this.isBatchLoading.set(false);
          this.isBatchDeparturesLoading$.next(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res: BatchDepartureResp | null) => {
          if (res) {
            this.batchDepartures.set(res);
          }
        },
      });
  }

  public setActiveTab(activeTab: number) {
    this.activeTab = activeTab;
  }

  private listenToRouter(): void {
    combineLatest([
      this.activatedRoute.paramMap,
      this.activatedRoute.queryParams,
    ])
      .pipe(
        filter(([paramMap, queryParams]) => {
          const batchId = paramMap.get('id');

          const isQueryParamsSame =
            this.queryParams &&
            this.queryParams.search === queryParams.search &&
            this.queryParams.perPage === queryParams.perPage &&
            this.queryParams.page === queryParams.page;

          if (isQueryParamsSame && batchId === this.batchId) {
            return false;
          }

          this.batchId = batchId;
          this.queryParams = { batchId, ...queryParams };
          return true;
        }),
        switchMap(() => {
          this.isBatchDeparturesLoading$.next(true);
          return this.batchesService.batchMailingIndex({
            batchId: this.batchId,
            perPage: this.queryParams.perPage,
            page: this.queryParams.page,
            search: this.queryParams.search,
          });
        }),
        finalize(() => {
          this.isBatchDeparturesLoading$.next(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (resp) => {
          this.batchDepartures.set(resp);
          this.isBatchDeparturesLoading$.next(false);
        },
      });
  }

  public async onQueryParamsChange(paginationParams: PaginationParams) {
    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { ...this.queryParams, ...paginationParams },
    });
  }

  public confirmDeleteDepartures(ids: number[]) {
    this.modalService.show(ConfirmDeleteModalComponent, {
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered',
      initialState: {
        title: this.translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.ACTIONS.DELETE_DEPARTURES_1'
        ),
        subTitle: this.translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.ACTIONS.CONFIRM_DELETING_DEPARTURES'
        ),
        submitBtnClass: 'button--filled',
        cancelBtnClass: 'button--text',
      },
    });

    this.modalService.onHidden
      .pipe(
        first(),
        filter((res) => res === 'delete')
      )
      .subscribe({
        next: () => this.deleteBatchMailings(ids),
      });
  }

  public deleteBatchMailings(ids: number[]) {
    const batchId = this.batchId;

    this.isBatchDeparturesLoading$.next(true);

    this.batchesService
      .removeMailings(batchId, ids)
      .pipe(
        switchMap(() =>
          this.batchesService.show({ batchId }).pipe(
            tap((data) => {
              this.batch.set(data);
            })
          )
        ),
        switchMap(() => this.batchesService.batchMailingIndex({ batchId })),
        finalize(() => {
          this.isBatchDeparturesLoading$.next(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (resp) => {
          this.batchDepartures.set({
            ...resp,
            data: resp.data.map(
              (batchDeparture) =>
                ({
                  ...batchDeparture,
                  selected: false,
                }) as BatchDepartureByIdRespWithSelected
            ),
          });
        },
      });
  }

  public back() {
    return this.router.navigateByUrl('/personal/batch/batches');
  }

  public formDocument(params: GenerateOneBatchDocumentEventParams) {
    const { departureId, s10Code, documentId, documentStatus, documentType } =
      params;

    if (documentId) {
      switch (documentStatus) {
        case BatchDocumentStatus.DONE:
          this.downloadDocuments(documentId, () =>
            this.runOneDocumentGeneration(departureId, s10Code, documentType)
          );
          return;

        case BatchDocumentStatus.ERROR:
          this.runOneDocumentGeneration(departureId, s10Code, documentType);
          return;

        case BatchDocumentStatus.PROCESSING:
        default:
          return;
      }
    } else {
      this.runOneDocumentGeneration(departureId, s10Code, documentType);
    }
  }

  private showProcessingNotificationIfNeeded(): void {
    const loadingBatchDocumentsStore =
      this.batchDocumentsStoreService.loadingBatchDocumentsStore();
    const individualDocumentsLastRun =
      loadingBatchDocumentsStore[loadingBatchDocumentsStore.length - 1];
    const lastRunLength = individualDocumentsLastRun.length;

    switch (lastRunLength) {
      case 1:
        return;

      case 2: {
        individualDocumentsLastRun.forEach((document) =>
          this.showOneDocumentProcessingNotification(document)
        );
        return;
      }

      default: {
        const documentInfo = individualDocumentsLastRun[lastRunLength - 1];
        this.showOneDocumentProcessingNotification(documentInfo);
        return;
      }
    }
  }

  private showOneDocumentProcessingNotification(
    documentInfo: BatchDocumentsStoreItem
  ): void {
    const title =
      documentInfo.documentType === 'shipping_label'
        ? this.translateService.instant(
            'BATCH_MAILING.BATCHES_TAB.NOTIFICATION.LABEL_FORM'
          )
        : this.translateService.instant(
            'BATCH_MAILING.BATCHES_TAB.NOTIFICATION.PARTIAL_RECEIPT_FORM'
          );

    this.batchDocumentToastService.showProcessingToast({
      title,
      subtitle: documentInfo.s10Code,
    });
  }

  /**
   * Method for batch generation.
   */
  private runAllLabelsGeneration(): void {
    const labelsCount =
      this.batchDepartures()?.meta?.total ??
      this.batchDepartures()?.data?.length ??
      0;
    const loadingAllLabelsInfo = {
      batchId: this.batch().id,
      documentStatus: 'processing',
      batchName: this.batch().name,
      labelsCount,
    } as BatchDocumentsStoreItem;

    this.batchDocumentsStoreService.pushNewLoadingStoreItem(
      loadingAllLabelsInfo,
      true
    );
    this.batchDocumentsStoreService.markLastDepartureDocumentsDownloadAsDone(
      this.batch().id
    );

    this.batchDocumentToastService.showProcessingToast({
      title: `${this.translateService.instant(
        'BATCH_MAILING.BATCHES_TAB.NOTIFICATION.ALL_FORM'
      )} ${labelsCount}`,
      subtitle: this.batch().name,
    });

    this.batchesService
      .generateAllBatchBlanks(this.batch().id)
      .pipe(
        tap((res) => this.batch.set(res)),
        catchError(() => {
          const errorDocumentInfo = {
            batchId: this.batch().id,
            documentStatus: 'error',
          } as BatchDocumentsStoreItem;

          this.batchDocumentsStoreService.updateStoreProcessingItem({
            documentInfo: errorDocumentInfo,
            errorOccurred: true,
            isAllDocuments: true,
          });

          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Method for one document generation.
   */
  private runOneDocumentGeneration(
    departureId: number,
    s10Code: string,
    documentType: DocumentType
  ): void {
    const loadingDocumentInfo = {
      batchId: this.batch().id,
      departureId,
      documentType,
      documentStatus: 'processing',
      s10Code,
    } as BatchDocumentsStoreItem;

    this.batchDocumentsStoreService.pushNewLoadingStoreItem(
      loadingDocumentInfo,
      false
    );
    this.showProcessingNotificationIfNeeded();

    this.batchesService
      .generateBatchDocument({
        batchId: this.batch().id,
        departureId,
        documentType,
        labelSize:
          documentType === 'shipping_label'
            ? this.labelSizeControl.value
            : null,
      })
      .pipe(
        catchError((error) => {
          this.presentErrorDialog(error);

          throw error;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res) => {
          const currentBatchDepartures = this.batchDepartures();

          const updatedData = currentBatchDepartures?.data.map((item) =>
            item.id === res.id
              ? {
                  ...item,
                  document: {
                    ...res.document,
                  },
                  documents: [...res.documents],
                }
              : item
          );

          this.batchDepartures.set({
            ...currentBatchDepartures,
            data: updatedData,
          });
        },
        error: () => {
          const erroredDocumentInfo = {
            batchId: this.batch().id,
            departureId,
            documentType: documentType,
            documentStatus: 'error',
            s10Code,
          } as BatchDocumentsStoreItem;

          this.batchDocumentsStoreService.updateStoreProcessingItem({
            documentInfo: erroredDocumentInfo,
            errorOccurred: true,
            isAllDocuments: false,
          });
        },
      });
  }

  /**
   * A method that download documents for a batch, partial receipts, and a shipping label.
   * @param documentId - The ID of the document to download.
   * @param generateDocument - The function to call if the download fails.
   */
  private downloadDocuments(
    documentId: number,
    generateDocument: () => void
  ): void {
    this.batchDocumentsService
      .download(documentId)
      .pipe(
        catchError(() => {
          generateDocument();

          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private listenToBatchRefresh(): void {
    this.batchRefreshService.refreshBatchData$
      .pipe(
        tap((batchData) => {
          if (batchData.batchId !== this.batch().id) {
            return;
          }

          if (batchData.departureId) {
            const documents = this.batchDepartures().data.find(
              (item) => item.id === batchData.departureId
            )?.documents;
            const updatedDocuments = documents?.map((item) =>
              item.type === batchData.documentType
                ? { ...item, status: batchData.documentStatus }
                : item
            );

            this.batchDepartures.set({
              ...this.batchDepartures(),
              data: this.batchDepartures().data.map((item) =>
                item.id === batchData.departureId
                  ? { ...item, documents: updatedDocuments }
                  : item
              ),
            });
          } else {
            this.batch.set({
              ...this.batch(),
              documents: {
                ...this.batch().documents,
                status: batchData.documentStatus,
              },
            });
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private presentErrorDialog(error: HttpErrorResponse): void {
    const swal = this.swalService.swal;

    swal.icon = 'error';
    swal.title = this.translateService.instant('AN_ERROR_HAS_OCCURRED');
    swal.text = error.error.message || error.message;
    swal.fire();
  }
}
